/**
 * P3-38: TimeControlWgt —— flux/heat 文本 + 加速按钮。
 *
 * 对齐 `js/time.js:1112-1168`。
 */
import { useGameTick } from "../../bridge/useGameTick";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import AccelerateTimeBtn from "./AccelerateTimeBtn";

interface TimeControlSnapshot {
  fluxText: string;
  heatText: string | null;
  heatOverMax: boolean;
  tooltip: string;
}

function getSnapshot(game: GamePage | null): TimeControlSnapshot | null {
  if (!game) {
    return null;
  }
  const temporalFlux = game.resPool?.get?.("temporalFlux");
  const fluxValue = temporalFlux?.value ?? 0;
  const fluxMax = temporalFlux?.maxValue ?? 0;
  const ticksPerSecond = game.ticksPerSecond ?? 5;

  const remainingSeconds = fluxValue / ticksPerSecond;
  const maxSeconds = fluxMax / ticksPerSecond;
  const fmtSeconds = (seconds: number) =>
    seconds < 1 ? "0" + t("unit.s") : game.toDisplaySeconds(seconds);

  let fluxText =
    t("time.flux") +
    ": " +
    game.getDisplayValueExt(fluxValue) +
    " / " +
    game.getDisplayValueExt(fluxMax);
  fluxText += " (" + fmtSeconds(remainingSeconds) + " / " + fmtSeconds(maxSeconds) + ")";

  let heatText: string | null = null;
  let heatOverMax = false;
  if (game.workshop?.get?.("chronoforge")?.researched) {
    const heat = game.time?.heat ?? 0;
    const heatMax = game.getEffect?.("heatMax") ?? 100;
    heatOverMax = heat > heatMax;
    const heatPerSecond = (game.getEffect?.("heatPerTick") ?? 0.01) * ticksPerSecond;
    const remainingHeatSeconds = heatPerSecond > 0 ? heat / heatPerSecond : 0;
    const maxHeatSeconds = heatPerSecond > 0 ? heatMax / heatPerSecond : 0;

    heatText =
      t("time.heat") +
      ": " +
      game.getDisplayValueExt(heat) +
      " / " +
      game.getDisplayValueExt(heatMax);
    heatText +=
      " (" +
      fmtSeconds(remainingHeatSeconds) +
      " / " +
      fmtSeconds(maxHeatSeconds) +
      ")";
  }

  let tooltip = t("time.flux.desc");
  if (game.workshop?.get?.("chronoforge")?.researched) {
    tooltip += "<br>" + t("time.chronoheat");
  }

  return { fluxText, heatText, heatOverMax, tooltip };
}

function snapEq(a: TimeControlSnapshot | null, b: TimeControlSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.fluxText === b.fluxText &&
    a.heatText === b.heatText &&
    a.heatOverMax === b.heatOverMax &&
    a.tooltip === b.tooltip
  );
}

export default function TimeControlWgt() {
  const snapshot = useGameTick(
    (game) => getSnapshot(game as GamePage | null),
    snapEq,
  );

  if (!snapshot) {
    return null;
  }

  return (
    <div>
      <span title={snapshot.tooltip}>
        {snapshot.fluxText}
        {snapshot.heatText && (
          <>
            <br />
            {snapshot.heatOverMax ? (
              <span style={{ color: "red" }}>{snapshot.heatText}</span>
            ) : (
              snapshot.heatText
            )}
          </>
        )}
      </span>
      <div style={{ paddingTop: "20px" }}>
        <AccelerateTimeBtn />
      </div>
    </div>
  );
}
